import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Heart,
  Users,
  Lightbulb,
  CheckCircle2,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  Clock,
  Pencil,
  Package,
} from "lucide-react";
import { trackPageView } from "@/lib/api";

const STEPS = [
  {
    number: "1",
    title: "Kijelölöd az irányt",
    description:
      "Válaszd ki, milyen helyzetre és milyen fókusz mentén szeretnéd a beszélgetésed elindítani.",
  },
  {
    number: "2",
    title: "A rendszer felépíti a kérdésívet",
    description:
      "A választásaid mentén a Kardiy egy tudásalapú, pszichológiai mintázatokra épülő struktúra szerint összeállítja a kérdések ívét.",
  },
  {
    number: "3",
    title: "Szabadon formálhatod",
    description:
      "Minden kérdést átírhatsz, finomíthatsz, vagy cserélhetsz. A végső pakli teljes mértékben a tiéd.",
  },
  {
    number: "4",
    title: "Fizikai pakliként érkezik",
    description:
      "Megrendelés után a kártyapakli közvetlenül a magyar nyomdában kerül legyártásra.",
  },
];

const BENEFITS = [
  {
    icon: Heart,
    title: "Tudatosan felépített ív",
    description:
      "A kérdések egymásra épülnek, és valódi beszélgetési dramaturgiát alkotnak.",
  },
  {
    icon: Users,
    title: "Kontextushoz hangolt tartalom",
    description:
      "A kiválasztott helyzet és fókusz meghatározza a kérdések karakterét.",
  },
  {
    icon: Lightbulb,
    title: "Fizikai, kézbe vehető termék",
    description: "A végleges pakli közvetlenül a nyomdából érkezik.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView("landing");
  }, []);

  return (
    <>
      <Helmet>
        <title>Kardiy | Egyedi beszélgetésindító kártyák</title>
        <meta
          name="description"
          content="AI-alapú Beszélgetésindító kártya-készítő. Válaszd ki,, a rendszer felépíti a kérdésívet, te magadra szabod, mi kinyomtatjuk és házhoz szállítjuk."
        />
        <meta
          property="og:title"
          content="Kardiy | Egyedi beszélgetésindító kártyák"
        />
        <meta
          property="og:description"
          content="AI-alapú Beszélgetésindító kártya-készítő. Válaszd ki,, a rendszer felépíti a kérdésívet, te magadra szabod, mi kinyomtatjuk és házhoz szállítjuk."
        />
        <meta property="og:url" content="https://kardiy.hu/" />
        <link rel="canonical" href="https://kardiy.hu/" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-stone-100">
          <div className="max-w-md mx-auto px-4 md:max-w-2xl lg:max-w-4xl">
            <div className="flex items-center h-16">
              <div className="flex items-center gap-3">
                {/* <img 
                src=""
                alt="Kardiy Logo" 
                className="w-12 h-12 rounded-xl object-cover"
              /> */}
                <span className="font-heading text-3xl font-bold text-[#292524]">
                  Kardiy
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Mobile First */}
        <section className="px-4 pt-4 pb-8 md:pt-16 md:pb-10">
          <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-4xl text-center">
            {/* Main Heading */}
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#292524] leading-tight px-2 mb-4">
              Beszélgetésindító kártyapakli, tudatosan felépítve,
              <br />
              <span className="text-[#FF6B6B]">percek alatt</span>
            </h1>

            {/* Subheading */}
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-6 max-w-xl mx-auto">
              A Kardiy egy előre felépített, pszichológiai mintázatra épülő
              struktúra mentén állítja össze a kérdésjavaslatot. Jelöld ki az
              irányt - mi felépítjük hozzá a beszélgetési ívet. Kevesebb mint 3
              perc alatt!
            </p>

            {/* CTA Button */}
            <Button
              onClick={() => navigate("/create")}
              data-testid="hero-cta-btn"
              className="bg-[#FF5252] hover:bg-[#FF6C6C] text-white font-bold text-lg rounded-full px-10 py-6 shadow-lg hover:shadow-xl active:scale-95 transition-all w-full md:w-auto"
            >
              Paklim összeállítása
              {/* <ArrowRight className="ml-2 w-5 h-5" /> */}
            </Button>

            {/* Trust indicator */}
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-stone-500">
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-[#FF52FF]" />
                Max 3 perc
              </span>
              <span className="flex items-center gap-1.5">
                <Pencil className="w-4 h-4 text-[#FF52FF]" />
                50 db szabadon szerkeszthető kártya
              </span>
              <span className="flex items-center gap-1.5">
                <Package className="w-4 h-4 text-[#FF52FF]" />
                Igazi kártyapakli a nyomdából
              </span>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 py-10 md:py-16 bg-white/50">
          <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-4xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#292524] text-center mb-12">
              Hogyan működik?
            </h2>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((step, i) => (
                <Card
                  key={i}
                  className="bg-white rounded-3xl shadow-sm border-0"
                  data-testid={`step-${i + 1}`}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-[#FF6B6B]/10 flex items-center justify-center mx-auto mb-4">
                      <span className="font-heading text-2xl font-bold text-[#FF6B6B]">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-[#292524] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-stone-500">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="px-4 py-16">
          <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-4xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#292524] text-center mb-12">
              Miért válaszd <br className="md:hidden" /> a Kardiy-t?
            </h2>

            <div className="grid gap-6 md:grid-cols-3">
              {BENEFITS.map((benefit, i) => (
                <Card
                  key={i}
                  className="bg-white rounded-3xl shadow-sm border-0"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-[#FCD34D]/20 flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-7 h-7 text-[#FF6B6B]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-[#292524] mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-stone-500">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section Above Footer */}
        <section className="px-4 py-16 bg-[#FF6B6B]/5">
          <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-4xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#292524] mb-4">
              Készen állsz a saját kártyáidra?
            </h2>
            <p className="text-stone-600 mb-8">
              Percek alatt jelöld ki az irányt. A Kardiy felépíti a tartalmat.
            </p>
            <Button
              onClick={() => navigate("/create")}
              data-testid="bottom-cta-btn"
              className="bg-[#FF5252] hover:bg-[#FF6C6C] text-white font-bold text-lg rounded-full px-10 py-6 shadow-lg hover:shadow-xl active:scale-95 transition-all"
            >
              Paklim összeállítása
              {/* <ArrowRight className="ml-2 w-5 h-5" /> */}
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-4 py-8 bg-[#292524]">
          <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-4xl">
            <div className="flex flex-col items-center gap-6">
              <div className="flex items-center gap-3">
                {/* <img 
                src="https://customer-assets.emergentagent.com/job_3593eb73-f1b7-41c3-8c2a-c1f976be0f73/artifacts/b7g8lt6c_kardiy_logo_v2.jpeg" 
                alt="Kardiy Logo" 
                className="w-10 h-10 rounded-lg object-cover"
              /> */}
                <span className="font-heading text-xl font-bold text-white">
                  Kardiy
                </span>
              </div>

              {/* Social Icons */}
              <div className="flex items-center gap-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61582621042921"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5 text-white" />
                </a>
                <a
                  href="https://www.instagram.com/_kardiy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5 text-white" />
                </a>
                <a
                  href="https://www.linkedin.com/company/kardiy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="w-5 h-5 text-white" />
                </a>
                <a
                  href="mailto:hello@kardiy.com"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  data-testid="social-email"
                >
                  <Mail className="w-5 h-5 text-white" />
                </a>
              </div>

              <p className="text-stone-400 text-sm">
                © 2026 Kardiy. Minden jog fenntartva.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
